// frontend/src/components/UrbanIA/UrbanIASidebar.js
// UrbanIA - Sidebar de Historial de Conversaciones (estilo Venice.ai)
// UrbanQ v1.9.9.1 - NextUnicode Corp
// ===============================================================

import React, { useState, useRef, useEffect } from 'react';

// ─────────────────────────────────────────────
// CONSTANTES
// ─────────────────────────────────────────────
const MAX_CONVERSATIONS = 10;
const MAX_FOLDERS = 5;

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────
const UrbanIASidebar = ({
  conversations = [],
  folders = [],
  activeConversationId,
  onSelectConversation,
  onNewChat,
  onDeleteConversation,
  onRenameConversation,
  onCreateFolder,
  onDeleteFolder,
  onMoveToFolder,
  isOverlay = false,
  onClose,
}) => {
  // Estados locales
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [contextMenu, setContextMenu] = useState(null); // { id, x, y }

  const editInputRef = useRef(null);
  const folderInputRef = useRef(null);

  // Focus en inputs
  useEffect(() => {
    if (editingId && editInputRef.current) editInputRef.current.focus();
  }, [editingId]);

  useEffect(() => {
    if (showNewFolder && folderInputRef.current) folderInputRef.current.focus();
  }, [showNewFolder]);

  // Cerrar context menu al hacer click fuera
  useEffect(() => {
    if (!contextMenu) return;
    const handler = () => setContextMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [contextMenu]);

  // ─────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────
  const truncateTitle = (title, max = 28) => {
    if (!title) return 'Nueva conversacion';
    return title.length > max ? title.substring(0, max) + '...' : title;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return d.toLocaleDateString('es-EC', { day: '2-digit', month: 'short' });
  };

  // Separar conversaciones en carpeta vs sin carpeta
  const ungroupedChats = conversations.filter(c => !c.folder_id);
  const isLimitReached = conversations.length >= MAX_CONVERSATIONS;
  const isFolderLimitReached = folders.length >= MAX_FOLDERS;

  // ─────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────
  const handleStartRename = (conv) => {
    setEditingId(conv.id);
    setEditTitle(conv.title || '');
    setContextMenu(null);
  };

  const handleFinishRename = () => {
    if (editingId && editTitle.trim()) {
      onRenameConversation(editingId, editTitle.trim());
    }
    setEditingId(null);
    setEditTitle('');
  };

  const handleRenameKeyDown = (e) => {
    if (e.key === 'Enter') handleFinishRename();
    if (e.key === 'Escape') { setEditingId(null); setEditTitle(''); }
  };

  const handleCreateFolder = () => {
    if (newFolderName.trim()) {
      onCreateFolder(newFolderName.trim());
      setNewFolderName('');
      setShowNewFolder(false);
    }
  };

  const handleFolderKeyDown = (e) => {
    if (e.key === 'Enter') handleCreateFolder();
    if (e.key === 'Escape') { setShowNewFolder(false); setNewFolderName(''); }
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({ ...prev, [folderId]: !prev[folderId] }));
  };

  const handleContextMenu = (e, convId) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ id: convId, x: e.clientX, y: e.clientY });
  };

  // ─────────────────────────────────────────
  // RENDER: Chat Item
  // ─────────────────────────────────────────
  const renderChatItem = (conv) => {
    const isActive = conv.id === activeConversationId;
    const isEditing = editingId === conv.id;

    return (
      <div
        key={conv.id}
        className={`urbania-sidebar-chat-item ${isActive ? 'active' : ''}`}
        onClick={() => !isEditing && onSelectConversation(conv.id)}
        onContextMenu={(e) => handleContextMenu(e, conv.id)}
      >
        <div className="urbania-sidebar-chat-icon">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" opacity="0.5">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
          </svg>
        </div>
        <div className="urbania-sidebar-chat-info">
          {isEditing ? (
            <input
              ref={editInputRef}
              className="urbania-sidebar-edit-input"
              value={editTitle}
              onChange={e => setEditTitle(e.target.value)}
              onBlur={handleFinishRename}
              onKeyDown={handleRenameKeyDown}
              maxLength={255}
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <>
              <span className="urbania-sidebar-chat-title">{truncateTitle(conv.title)}</span>
              <span className="urbania-sidebar-chat-meta">
                {conv.message_count || 0} msgs · {formatDate(conv.updated_at)}
              </span>
            </>
          )}
        </div>
        {!isEditing && (
          <div className="urbania-sidebar-chat-actions">
            <button
              className="urbania-sidebar-action-btn"
              onClick={(e) => { e.stopPropagation(); handleStartRename(conv); }}
              title="Renombrar"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
            </button>
            <button
              className="urbania-sidebar-action-btn urbania-sidebar-action-delete"
              onClick={(e) => { e.stopPropagation(); onDeleteConversation(conv.id); }}
              title="Eliminar"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
            </button>
          </div>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────
  // RENDER PRINCIPAL
  // ─────────────────────────────────────────
  return (
    <div className={`urbania-sidebar ${isOverlay ? 'urbania-sidebar-overlay' : ''}`}>
      {/* Header */}
      <div className="urbania-sidebar-header">
        <h3 className="urbania-sidebar-title-main">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" style={{ opacity: 0.7 }}>
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
          </svg>
          Historial
        </h3>
        {isOverlay && (
          <button className="urbania-sidebar-close" onClick={onClose} title="Cerrar historial">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        )}
      </div>

      {/* Boton Nuevo Chat */}
      <div className="urbania-sidebar-new-chat">
        <button
          className={`urbania-sidebar-new-btn ${isLimitReached ? 'disabled' : ''}`}
          onClick={() => !isLimitReached && onNewChat()}
          disabled={isLimitReached}
          title={isLimitReached ? `Limite de ${MAX_CONVERSATIONS} chats alcanzado` : 'Nuevo chat'}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
          </svg>
          Nuevo chat
        </button>
        {isLimitReached && (
          <span className="urbania-sidebar-limit-badge">
            {conversations.length}/{MAX_CONVERSATIONS} - Elimina uno
          </span>
        )}
      </div>

      {/* Contenido scrollable */}
      <div className="urbania-sidebar-content">
        {/* Chats sin folder */}
        {ungroupedChats.length > 0 && (
          <div className="urbania-sidebar-section">
            <div className="urbania-sidebar-section-header">
              <span className="urbania-sidebar-section-title">CHATS</span>
              <span className="urbania-sidebar-section-count">{ungroupedChats.length}</span>
            </div>
            {ungroupedChats.map(conv => renderChatItem(conv))}
          </div>
        )}

        {/* Folders */}
        {folders.length > 0 && (
          <div className="urbania-sidebar-section">
            <div className="urbania-sidebar-section-header">
              <span className="urbania-sidebar-section-title">CARPETAS</span>
              <span className="urbania-sidebar-section-count">{folders.length}</span>
            </div>
            {folders.map(folder => {
              const folderChats = conversations.filter(c => c.folder_id === folder.id);
              const isExpanded = expandedFolders[folder.id];

              return (
                <div key={folder.id} className="urbania-sidebar-folder">
                  <div
                    className="urbania-sidebar-folder-header"
                    onClick={() => toggleFolder(folder.id)}
                  >
                    <span className={`urbania-sidebar-folder-arrow ${isExpanded ? 'expanded' : ''}`}>
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                        <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                      </svg>
                    </span>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{ opacity: 0.5 }}>
                      <path d="M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
                    </svg>
                    <span className="urbania-sidebar-folder-name">{folder.name}</span>
                    <span className="urbania-sidebar-folder-count">{folderChats.length}</span>
                    <button
                      className="urbania-sidebar-action-btn urbania-sidebar-action-delete"
                      onClick={(e) => { e.stopPropagation(); onDeleteFolder(folder.id); }}
                      title="Eliminar carpeta"
                    >
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                      </svg>
                    </button>
                  </div>
                  {isExpanded && (
                    <div className="urbania-sidebar-folder-chats">
                      {folderChats.length > 0 ? (
                        folderChats.map(conv => renderChatItem(conv))
                      ) : (
                        <span className="urbania-sidebar-empty-folder">Sin chats</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Nuevo folder */}
        <div className="urbania-sidebar-section">
          {showNewFolder ? (
            <div className="urbania-sidebar-new-folder-input">
              <input
                ref={folderInputRef}
                className="urbania-sidebar-edit-input"
                placeholder="Nombre de la carpeta..."
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={handleFolderKeyDown}
                onBlur={() => { if (!newFolderName.trim()) setShowNewFolder(false); }}
                maxLength={100}
              />
              <button className="urbania-sidebar-folder-save" onClick={handleCreateFolder}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              </button>
            </div>
          ) : (
            <button
              className={`urbania-sidebar-add-folder ${isFolderLimitReached ? 'disabled' : ''}`}
              onClick={() => !isFolderLimitReached && setShowNewFolder(true)}
              disabled={isFolderLimitReached}
              title={isFolderLimitReached ? `Limite de ${MAX_FOLDERS} carpetas` : 'Nueva carpeta'}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                <path d="M20 6h-8l-2-2H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm0 12H4V8h16v10zm-8-4h2v2h2v-2h2v-2h-2v-2h-2v2h-2v2z"/>
              </svg>
              Nueva carpeta
              {isFolderLimitReached && <span className="urbania-sidebar-limit-small">{folders.length}/{MAX_FOLDERS}</span>}
            </button>
          )}
        </div>

        {/* Vacio */}
        {conversations.length === 0 && (
          <div className="urbania-sidebar-empty">
            <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor" style={{ opacity: 0.15 }}>
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
            </svg>
            <p>No tienes conversaciones guardadas</p>
            <span>Inicia un nuevo chat para comenzar</span>
          </div>
        )}
      </div>

      {/* Context Menu (click derecho) */}
      {contextMenu && (
        <div
          className="urbania-sidebar-context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={e => e.stopPropagation()}
        >
          <button onClick={() => {
            const conv = conversations.find(c => c.id === contextMenu.id);
            if (conv) handleStartRename(conv);
          }}>
            Renombrar
          </button>
          {folders.length > 0 && (
            <>
              <div className="urbania-sidebar-context-divider" />
              {folders.map(f => (
                <button key={f.id} onClick={() => {
                  onMoveToFolder(contextMenu.id, f.id);
                  setContextMenu(null);
                }}>
                  Mover a {f.name}
                </button>
              ))}
              <button onClick={() => {
                onMoveToFolder(contextMenu.id, null);
                setContextMenu(null);
              }}>
                Quitar de carpeta
              </button>
            </>
          )}
          <div className="urbania-sidebar-context-divider" />
          <button
            className="urbania-sidebar-context-delete"
            onClick={() => {
              onDeleteConversation(contextMenu.id);
              setContextMenu(null);
            }}
          >
            Eliminar
          </button>
        </div>
      )}
    </div>
  );
};

export default UrbanIASidebar;
